import React from "react";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { FREDOKA, NO, Mascot, ContinueSlot, ContinueButton } from "../NOKit";

// iOS NOMeetPixleyStep port: character reveal with confetti.
const CONFETTI = [
  { left: "8%", top: "6%", bg: "#FF6B9A", delay: "0s" },
  { left: "88%", top: "10%", bg: "#FFD76A", delay: "0.2s" },
  { left: "16%", top: "78%", bg: "#3EDC8F", delay: "0.4s" },
  { left: "80%", top: "72%", bg: "#8A5CF6", delay: "0.1s" },
  { left: "50%", top: "2%", bg: "#00CAFF", delay: "0.3s" },
  { left: "4%", top: "42%", bg: "#FFC93C", delay: "0.5s" },
  { left: "94%", top: "44%", bg: "#FF6B9A", delay: "0.6s" },
];

const MeetStep = () => {
  const { username, profilePicture, originalProfileImage, nextStep, avatarPending } = useOnboarding();

  if (avatarPending) {
    return (
      <div className="w-full max-w-xl mx-auto flex flex-col items-center px-4 animate-fadeIn text-center">
        <Mascot size={200} clip="panda_draw" />
        <h1
          style={{
            ...FREDOKA,
            color: NO.ink,
            fontWeight: 700,
            fontSize: "clamp(28px, 5vw, 34px)",
            marginTop: 6,
          }}
        >
          Bringing <span style={{ color: NO.accent }}>{username || "your hero"}</span> to life…
        </h1>
        <p style={{ ...FREDOKA, color: NO.subtitle, fontSize: 17, marginTop: 10 }}>
          Adding the finishing touches to their avatar.
        </p>
        <div
          style={{
            marginTop: 26,
            width: 220,
            height: 10,
            borderRadius: 999,
            background: NO.track,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: "40%",
              borderRadius: 999,
              background: `linear-gradient(90deg, ${NO.accent}, #00CAFF)`,
              animation: "ob-indeterminate 1.4s ease-in-out infinite",
            }}
          />
        </div>
      </div>
    );
  }
  // "default:<initial>" is the skip-photo sentinel, not a URL — fall back to
  // the mascot in that case.
  const isRealImage = (v) => v && !String(v).startsWith("default:");
  const image = [profilePicture, originalProfileImage].find(isRealImage) || null;

  return (
    <div className="w-full max-w-xl mx-auto flex flex-col items-center px-4 animate-fadeIn">
      <div className="relative" style={{ padding: 24 }}>
        {CONFETTI.map((c, i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              left: c.left,
              top: c.top,
              width: 10,
              height: 10,
              background: c.bg,
              animation: `ob-float 2.6s ease-in-out ${c.delay} infinite alternate`,
            }}
          />
        ))}
        <div
          className="relative overflow-hidden"
          style={{
            width: 270,
            height: 270,
            borderRadius: 30,
            border: "5px solid #FFFFFF",
            boxShadow: "0 18px 40px rgba(0,101,248,0.25)",
            background: "#E3F0FF",
          }}
        >
          {image ? (
            <img src={image} alt={username} className="w-full h-full object-cover" />
          ) : (
            <img
              src="/assets/pixley-panda-nobg.png"
              alt=""
              className="w-full h-full object-contain p-6"
              onError={(e) => {
                e.target.src = "/assets/pixley-panda.png";
              }}
            />
          )}
        </div>
      </div>

      <div className="w-full text-center" style={{ marginTop: 4 }}>
        <div
          style={{
            ...FREDOKA,
            color: NO.accent,
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: 2,
            textTransform: "uppercase",
          }}
        >
          Meet your new friend
        </div>
        <h1
          style={{
            ...FREDOKA,
            color: NO.ink,
            fontWeight: 700,
            fontSize: "clamp(30px, 5vw, 38px)",
            lineHeight: 1.15,
            marginTop: 6,
          }}
        >
          This is <span style={{ color: NO.accent }}>{username || "your hero"}!</span>
        </h1>
        <p
          className="mx-auto"
          style={{ ...FREDOKA, color: NO.subtitle, fontSize: 17, marginTop: 8, maxWidth: 420 }}
        >
          The hero of every cartoon and bedtime story, made just for them.
        </p>
      </div>

      <ContinueSlot>
        <ContinueButton
          onClick={nextStep}
          label={username ? `Show this to ${username}` : "Continue"}
        />
      </ContinueSlot>
    </div>
  );
};

export default MeetStep;
